import { FirebaseAuthTypes, sendPasswordResetEmail as sendPasswordResetEmailMobile } from '@react-native-firebase/auth';
import { captureException } from '@sentry/react-native';
import { router } from 'expo-router';
import { Auth, sendPasswordResetEmail as sendPasswordResetEmailWeb } from 'firebase/auth';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import LoginHeader from '@/components/LoginHeader';
import Text from '@/components/Text';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { auth } from '@/firebaseConfig';
import { useColor } from '@/hooks/useColor';
import { NpoForgotPasswordData, npoForgotPasswordResolver } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';

const ForgotPassword = () => {
  const colors = useColor();
  const [isLoading, setIsLoading] = useState(false);
  const { showError, showSuccess } = useEndFlowModal();

  //Locales
  const title = i18n.t('sign_in.nonProfit.forgot_password.title');
  const success = i18n.t('end_flow.success_title');
  const successMessage = i18n.t('sign_in.nonProfit.forgot_password.success_message');
  const failure = i18n.t('end_flow.error_title');
  const failureMessage = i18n.t('sign_in.nonProfit.forgot_password.failure_message');
  const email = i18n.t('mix.email');

  const {
    control,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm<NpoForgotPasswordData>({
    mode: 'onSubmit',
    resolver: npoForgotPasswordResolver
  });

  const onSubmit: SubmitHandler<NpoForgotPasswordData> = async (data) => {
    setIsLoading(true);
    const emailValue = data.email.toLocaleLowerCase();

    if (Platform.OS === 'web') {
      await sendPasswordResetEmailWeb(auth() as Auth, emailValue)
        .then(() => {
          setIsLoading(false);
          showSuccess({
            title: success,
            message: successMessage
          });
          router.back();
        })
        .catch((error) => {
          console.error(error);
          captureException(error);
          setIsLoading(false);
          showError({
            title: failure,
            message: failureMessage
          });
        });
    } else {
      await sendPasswordResetEmailMobile(auth() as FirebaseAuthTypes.Module, emailValue)
        .then(() => {
          setIsLoading(false);
          showSuccess({
            title: success,
            message: successMessage
          });
          router.back();
        })
        .catch((error) => {
          console.error(error);
          captureException(error);
          setIsLoading(false);
          showError({
            title: failure,
            message: failureMessage
          });
        });
    }
  };

  return (
    <FormWrapper keyboardVerticalOffset={0}>
      <SafeAreaView style={styles.content}>
        <View style={styles.container}>
          <LoginHeader />
          <Text style={[styles.title, { color: colors.titleText }]} fontWeight="700">
            {title}
          </Text>
          <View style={styles.formContainer}>
            <TextInputField name="email" control={control} placeholder={email} type="email-address" />
            {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

            <BrandedButton style={styles.btn} onPress={handleSubmit(onSubmit)} disabled={!isValid} loading={isLoading}>
              {i18n.t('mix.continue')}
            </BrandedButton>
          </View>
        </View>
      </SafeAreaView>
    </FormWrapper>
  );
};

export default ForgotPassword;

const styles = StyleSheet.create({
  content: {
    flex: 1
  },

  container: {
    flex: 1,
    paddingTop: 25
  },

  title: {
    fontSize: 22,
    marginBottom: 50
  },

  formContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'center'
  },
  btn: {
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});
